import { useEffect, useState } from "react";
import {
  CheckCircle2,
  FileText,
  MessageSquare,
  Plus,
  Send,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader } from "../../components/shared";

const EMPTY_TYPE = {
  code: "",
  name: "",
  category: "Formal Society Request",
  description: "",
  instructions: "",
  active: true,
  eligible_relationships: ["OWNER", "JOINT_OWNER", "TENANT"],
  fields: [],
  document_rules: [],
  approval_required: true,
  approval_role_id: "",
  final_document_required: false,
  document_prefix: "REQ",
  expected_processing_days: 7,
  clarification_allowed: true,
  rejection_reason_required: true,
  show_outstanding_to_reviewer: false,
  fee_metadata: {},
  signatory_label: "Authorized Society Signatory",
};

export default function SocietyFormalRequests() {
  const [data, setData] = useState(null);
  const [requestType, setRequestType] = useState(EMPTY_TYPE);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [field, setField] = useState({
    key: "",
    label: "",
    type: "TEXT",
    required: false,
  });
  const [documentRule, setDocumentRule] = useState({
    key: "",
    label: "",
    required: true,
  });
  const [message, setMessage] = useState("");
  const [documentText, setDocumentText] = useState("");

  const load = async () => {
    try {
      const response = await api.get("/society-requests/bootstrap");
      setData(response.data);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load formal requests",
      );
      setData({ request_types: [], requests: [] });
    }
  };
  useEffect(() => {
    load();
  }, []);
  const loadDetail = async (item) => {
    setSelected(item);
    try {
      const response = await api.get(
        `/society-requests/requests/${item.request_id}`,
      );
      setDetail(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load request");
    }
  };
  const run = async (call, success) => {
    try {
      const result = await call();
      toast.success(success);
      await load();
      if (selected) await loadDetail(selected);
      return result.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update request");
      return null;
    }
  };
  const saveType = async () => {
    const current = requestType.request_type_id;
    const result = await run(
      () =>
        current
          ? api.patch(`/society-requests/request-types/${current}`, requestType)
          : api.post("/society-requests/request-types", requestType),
      "Request type saved",
    );
    if (result) setRequestType(result);
  };
  if (!data) return <Loading />;
  const request = detail?.request || selected;
  return (
    <div data-testid="society-formal-requests-page">
      <PageHeader
        subtitle={
          "Review formal applications, issue Society documents, and preserve an auditable " +
          "approval record."
        }
        title="Resident Requests"
      />
      <section
        className="border border-slate-200 bg-white p-5"
        data-testid="request-type-master"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-semibold">
            Request Type Master
          </h2>
          <Button
            data-testid="request-type-new"
            onClick={() => setRequestType(EMPTY_TYPE)}
            size="sm"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            New type
          </Button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="request-type-existing"
            onChange={(event) => {
              const item = data.request_types.find(
                (row) => row.request_type_id === event.target.value,
              );
              if (item) setRequestType(item);
            }}
            value={requestType.request_type_id || ""}
          >
            <option value="">Create new type</option>
            {data.request_types.map((item) => (
              <option key={item.request_type_id} value={item.request_type_id}>
                {item.name}
              </option>
            ))}
          </select>
          <Input
            data-testid="request-type-name"
            onChange={(event) =>
              setRequestType({ ...requestType, name: event.target.value })
            }
            placeholder="Request type name"
            value={requestType.name}
          />
          <Input
            data-testid="request-type-code"
            onChange={(event) =>
              setRequestType({
                ...requestType,
                code: event.target.value.toUpperCase(),
              })
            }
            placeholder="CODE"
            value={requestType.code}
          />
          <Input
            data-testid="request-type-prefix"
            onChange={(event) =>
              setRequestType({
                ...requestType,
                document_prefix: event.target.value.toUpperCase(),
              })
            }
            placeholder="Document prefix"
            value={requestType.document_prefix}
          />
          <Textarea
            className="sm:col-span-2"
            data-testid="request-type-instructions"
            onChange={(event) =>
              setRequestType({
                ...requestType,
                instructions: event.target.value,
              })
            }
            placeholder="Resident instructions"
            value={requestType.instructions}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          {[
            "OWNER",
            "JOINT_OWNER",
            "TENANT",
            "FAMILY_MEMBER",
            "AUTHORIZED_OCCUPANT",
          ].map((role) => (
            <label className="flex items-center gap-2" key={role}>
              <input
                checked={requestType.eligible_relationships.includes(role)}
                data-testid={`request-type-role-${role}`}
                onChange={(event) =>
                  setRequestType({
                    ...requestType,
                    eligible_relationships: event.target.checked
                      ? [...requestType.eligible_relationships, role]
                      : requestType.eligible_relationships.filter(
                          (item) => item !== role,
                        ),
                  })
                }
                type="checkbox"
              />
              {role.replaceAll("_", " ")}
            </label>
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <Input
            data-testid="request-field-key"
            onChange={(event) =>
              setField({ ...field, key: event.target.value })
            }
            placeholder="field_key"
            value={field.key}
          />
          <Input
            data-testid="request-field-label"
            onChange={(event) =>
              setField({ ...field, label: event.target.value })
            }
            placeholder="Field label"
            value={field.label}
          />
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="request-field-type"
            onChange={(event) =>
              setField({ ...field, type: event.target.value })
            }
            value={field.type}
          >
            {[
              "TEXT",
              "LONG_TEXT",
              "NUMBER",
              "DATE",
              "DROPDOWN",
              "RADIO",
              "CHECKBOX",
              "YES_NO",
            ].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
          <Button
            data-testid="request-field-add"
            onClick={() => {
              if (field.key && field.label) {
                setRequestType({
                  ...requestType,
                  fields: [...requestType.fields, field],
                });
                setField({ key: "", label: "", type: "TEXT", required: false });
              }
            }}
            size="sm"
            variant="outline"
          >
            Add field
          </Button>
        </div>
        <div
          className="mt-2 text-xs text-slate-600"
          data-testid="request-type-fields"
        >
          {requestType.fields
            .map((item) => `${item.label} (${item.type})`)
            .join(" · ") || "No configured fields"}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Input
            data-testid="request-document-key"
            onChange={(event) =>
              setDocumentRule({ ...documentRule, key: event.target.value })
            }
            placeholder="document_key"
            value={documentRule.key}
          />
          <Input
            data-testid="request-document-label"
            onChange={(event) =>
              setDocumentRule({ ...documentRule, label: event.target.value })
            }
            placeholder="Required document"
            value={documentRule.label}
          />
          <Button
            data-testid="request-document-add"
            onClick={() => {
              if (documentRule.key && documentRule.label) {
                setRequestType({
                  ...requestType,
                  document_rules: [...requestType.document_rules, documentRule],
                });
                setDocumentRule({ key: "", label: "", required: true });
              }
            }}
            size="sm"
            variant="outline"
          >
            Add document rule
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <label>
            <input
              checked={requestType.active}
              data-testid="request-type-active"
              onChange={(event) =>
                setRequestType({ ...requestType, active: event.target.checked })
              }
              type="checkbox"
            />{" "}
            Active
          </label>
          <label>
            <input
              checked={requestType.final_document_required}
              data-testid="request-type-final-document"
              onChange={(event) =>
                setRequestType({
                  ...requestType,
                  final_document_required: event.target.checked,
                })
              }
              type="checkbox"
            />{" "}
            Final document required
          </label>
          <label>
            <input
              checked={requestType.show_outstanding_to_reviewer}
              data-testid="request-type-outstanding"
              onChange={(event) =>
                setRequestType({
                  ...requestType,
                  show_outstanding_to_reviewer: event.target.checked,
                })
              }
              type="checkbox"
            />{" "}
            Show outstanding to reviewer
          </label>
        </div>
        <Button
          className="mt-5"
          data-testid="request-type-save"
          onClick={saveType}
        >
          Save request type
        </Button>
      </section>
      <section className="mt-8" data-testid="society-request-inbox">
        <h2 className="font-display text-2xl font-semibold">Request Inbox</h2>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {data.requests.map((item) => (
            <button
              className="border border-slate-200 bg-white p-4 text-left hover:border-blue-500"
              data-testid={`society-request-${item.request_id}`}
              key={item.request_id}
              onClick={() => loadDetail(item)}
            >
              <p className="text-xs font-semibold text-blue-700">
                {item.status}
              </p>
              <h3 className="mt-1 font-display text-lg font-semibold">
                {item.request_type_snapshot?.name}
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                {item.request_number} · {item.applicant_snapshot?.name} ·{" "}
                {item.unit_snapshot?.unit_number}
              </p>
            </button>
          ))}
        </div>
      </section>
      {request && (
        <section
          className="mt-8 border-t border-slate-200 pt-6"
          data-testid="society-request-detail"
        >
          <h2 className="font-display text-2xl font-semibold">
            {request.request_number} · {request.status}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Applicant: {request.applicant_snapshot?.name} · Unit{" "}
            {request.unit_snapshot?.unit_number}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {["SUBMITTED", "RESUBMITTED"].includes(request.status) && (
              <Button
                data-testid="request-start-review"
                onClick={() =>
                  run(
                    () =>
                      api.post(
                        `/society-requests/requests/${request.request_id}/start-review`,
                      ),
                    "Review started",
                  )
                }
                size="sm"
              >
                Start review
              </Button>
            )}
            {request.status === "UNDER_REVIEW" && (
              <>
                <Button
                  data-testid="request-clarification"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-requests/requests/${request.request_id}/clarification`,
                          { body: message },
                        ),
                      "Clarification requested",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  <MessageSquare className="h-4 w-4" />
                  Request clarification
                </Button>
                <Button
                  data-testid="request-approve"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-requests/requests/${request.request_id}/approve`,
                          { note: message },
                        ),
                      "Request approved",
                    )
                  }
                  size="sm"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Approve
                </Button>
                <Button
                  data-testid="request-reject"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-requests/requests/${request.request_id}/reject`,
                          { reason: message },
                        ),
                      "Request rejected",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </Button>
              </>
            )}{" "}
            {request.status === "APPROVED" && (
              <Button
                data-testid="request-generate-document"
                onClick={() =>
                  run(
                    () =>
                      api.post(
                        `/society-requests/requests/${request.request_id}/generate-document`,
                        { body: documentText },
                      ),
                    "Document issued",
                  )
                }
                size="sm"
              >
                <FileText className="h-4 w-4" />
                Generate document
              </Button>
            )}{" "}
            {["APPROVED", "DOCUMENT_ISSUED"].includes(request.status) && (
              <Button
                data-testid="request-complete"
                onClick={() =>
                  run(
                    () =>
                      api.post(
                        `/society-requests/requests/${request.request_id}/complete`,
                      ),
                    "Request completed",
                  )
                }
                size="sm"
                variant="outline"
              >
                Complete
              </Button>
            )}
          </div>
          <Textarea
            className="mt-4"
            data-testid="request-review-message"
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Clarification, approval note, or resident-safe rejection reason"
            value={message}
          />
          <Textarea
            className="mt-3"
            data-testid="request-document-text"
            onChange={(event) => setDocumentText(event.target.value)}
            placeholder="Issued document text"
            value={documentText}
          />
          <Button
            className="mt-3"
            data-testid="request-internal-note"
            onClick={() =>
              run(
                () =>
                  api.post(
                    `/society-requests/requests/${request.request_id}/internal-note`,
                    { body: message },
                  ),
                "Internal note saved",
              )
            }
            size="sm"
            variant="outline"
          >
            Add internal note
          </Button>
          {detail?.outstanding && (
            <p
              className="mt-4 text-sm font-semibold text-amber-700"
              data-testid="request-outstanding"
            >
              Decision support only: current outstanding ₹
              {detail.outstanding.current_outstanding}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
